<template>
  <div>
    <ts-page-title
      :title="$t('transectionBackDate.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('transectionBackDate.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <div class="row">
          <div class="col-md-3 form-group ts-px-2">
            <div class="ts-flex ts-justify-between">
              <label class="form-col-label control-label required ts-pr-4">{{
                $t('transectionBackDate.dateRange')
              }}</label>
              <a-radio-group v-model="model.date_type" size="small">
                <a-radio-button value="transaction_date"
                  >{{ $t('transectionBackDate.transactionDate') }}</a-radio-button
                >
                <a-radio-button value="created_date"
                  >{{ $t('transectionBackDate.createdDate') }}</a-radio-button
                >
              </a-radio-group>
            </div>
            <a-range-picker
              style="width:100%;"
              v-model="model.date_range"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              :allowClear="false"
            />
            <div class="validate" v-if="errors.has('date_range')">
              {{ errors.first('date_range') }}
            </div>
          </div>
          <div class="col-md-3 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('transectionBackDate.branchName')
            }}</label>
            <a-select
              style="width: 100%; font-size: 15px"
              mode="multiple"
              placeholder="All"
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              v-model="model.branch_id"
              :options="branchList"
              @change="onChangeBranch"
            ></a-select>
          </div>
          <div class="col-md-3 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('transectionBackDate.locationName')
            }}</label>
            <a-select
              v-model="model.location_id"
              mode="multiple"
              :defaultActiveFirstOption="true"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              :options="locations"
              :placeholder="$t('all')"
              class="ts-w-full"
            ></a-select>
          </div>
          <div class="col-md-3 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-2">{{
              $t('transectionBackDate.transectionType')
            }}</label>
            <a-select
              v-model="model.transaction_type"
              mode="multiple"
              :defaultActiveFirstOption="true"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              :options="transectionTypes"
              :placeholder="$t('all')"
              class="ts-w-full"
            ></a-select>
          </div>
          <div class="col-md-3 form-group ts-px-2">
            <label class="form-col-label control-label ts-mb-4"></label>
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 100%;"
              class="inline-block"
            />
          </div>
          <div class="col-md-2 form-group ts-px-2 ts-pt-6">
            <Button
              :disabled="loading"
              :loading="loading"
              class="ts-mb-1"
              type="primary"
              @click="fetchData"
              >{{ $t('view') }}
            </Button>
            <Button
              class="ts-mb-1 ts-ml-3"
              type="success"
              :disabled="exporting"
              :loading="exporting"
              ghost
              @click="exportExcel"
            >
              <Icon type="ios-document-outline" />
              {{ $t('excel') }}
            </Button>
          </div>
        </div>
      </ts-panel-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        max-height="700"
        border
      >
      </Table>
      <div class="ts-flex ts-items-center ts-justify-end ts-p-4">
        <ts-i-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-i-pagination>
      </div>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { trim, debounce } from 'lodash'

export default {
  name: 'transaction-back-date',
  components: {},
  data() {
    return {
      errors: new Errors(),
      loading: false,
      exporting: false,
      locationLoading: false,
      branchList: [],
      locations: [],
      transectionTypes: [
        { value: 'Whole Sale', label: 'Whole Sale' },
        { value: 'Transfer', label: 'Transfer' },
        { value: 'PI', label: 'PI' },
        { value: 'Sale Contract', label: 'Sale Contract' },
        { value: 'Receipt', label: 'Receipt' },
        { value: 'Quotation', label: 'Quotation' }
      ],
      model: {
        date_range: [
          moment()
            .startOf('month')
            .format('DD-MM-YYYY'),
          moment()
            .endOf('month')
            .format('DD-MM-YYYY')
        ],
        date_type: 'transaction_date',
        branch_id: undefined,
        location_id: undefined,
        transaction_type: undefined
      }
    }
  },
  computed: {
    ...mapState('report/transectionBackDate', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.report.transectionBackDate.search
      },
      set(newValue) {
        this.$store.commit('report/transectionBackDate/SET_SEARCH', newValue)
        this.$store.commit('report/transectionBackDate/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          title: this.$t('transectionBackDate.branchName'),
          key: 'branch_name',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.locationName'),
          key: 'location_name',
          minWidth: 200
        },
        {
          title: this.$t('transectionBackDate.transectionNo'),
          key: 'transaction_no',
          align: 'center',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.transectionType'),
          key: 'transaction_type',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.transectionDate'),
          key: 'transaction_date_format',
          align: 'center',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.createdBy'),
          key: 'created_by',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.createdDate'),
          key: 'created_date',
          align: 'center',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.comfirmedBy'),
          key: 'confirmed_by',
          minWidth: 150
        },
        {
          title: this.$t('transectionBackDate.comfirmedDate'),
          key: 'confirmed_date',
          align: 'center',
          minWidth: 150
        }
      ]
    }
  },
  created() {
    this.fetchResource()
  },
  methods: {
    async fetchResource() {
      this.loading = true
      await this.$store
        .dispatch('security/user/getBranchAssigned')
        .then(response => {
          this.branchList = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
      this.loading = false
    },
    onChangeBranch() {
      this.locationLoading = true
      this.locations = []
      this.model.location_id = undefined
      this.$store
        .dispatch('report/resource/getLocation', {
          branch_id: this.model.branch_id
        })
        .then(response => {
          this.locations = response.data.map(el => ({
            value: el.location_id,
            label: el.location_name_en
          }))
        })
        .finally(() => {
          this.locationLoading = false
        })
    },
    async fetchData(attributes) {
      this.loading = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/transectionBackDate/fetch', {
          date_range: this.model.date_range,
          branch_id: this.model.branch_id,
          location_id: this.model.location_id,
          transaction_type: this.model.transaction_type,
          date_type: this.model.date_type,
          ...attributes
        })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.errors = new Errors(error.errors)
          this.notice({ type: 'error', text: error.message })
        })
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/transectionBackDate/transectionBackDateExcel', {
          date_range: this.model.date_range,
          branch_id: this.model.branch_id,
          location_id: this.model.location_id,
          transaction_type: this.model.transaction_type,
          date_type: this.model.date_type
        })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'DAILY ATTENDANCES',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchResource()
    })
  }
}
</script>
